// Review HTML renderer (§4 view / §10 pass6). Pure: Script v3 doc → the verified
// cezhi-verify review UI (dual-tab 剧本审阅 / 资产总览, left video + right script
// rows, click a row to seek, per-actor state×episode matrix, scene chips). The
// HTML/CSS/JS is ported VERBATIM from the scaffold's 06_build_review_html; only
// the data it reads is transformed here from v3 into the shape that UI expects
// (asset_name / actor_names / actor_states / scene_num / location_name /
// action.extend.timestamp). Video is referenced relatively as src/ep_NNN.mp4
// (the view command symlinks the media into the workspace).

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => typeof v === "object" && v !== null && !Array.isArray(v);
const isArr = (v: unknown): v is unknown[] => Array.isArray(v);
const S = (v: unknown): string => (typeof v === "string" ? v : "");

const SPACE_EN_TO_ZH: Record<string, string> = { interior: "内", exterior: "外" };
const TIME_EN_TO_ZH: Record<string, string> = { day: "日", night: "夜" };
const epNum = (episodeId: string): number => { const m = episodeId.match(/(\d+)/); return m ? Number(m[1]) : 0; };
const scnNum = (sceneId: string): number => { const m = sceneId.match(/(\d+)/); return m ? Number(m[1]) : 0; };

function nameMap(list: unknown, idField: string, nameField: string): Map<string, string> {
  const m = new Map<string, string>();
  if (isArr(list)) for (const a of list) if (isObj(a) && typeof a[idField] === "string") m.set(a[idField] as string, S(a[nameField]));
  return m;
}
function stateNameMap(list: unknown, idField: string): Map<string, Map<string, string>> {
  const m = new Map<string, Map<string, string>>();
  if (isArr(list)) for (const a of list) {
    if (!isObj(a) || typeof a[idField] !== "string") continue;
    const sm = new Map<string, string>();
    for (const st of isArr(a.states) ? a.states : []) if (isObj(st) && typeof st.state_id === "string") sm.set(st.state_id, S(st.state_name));
    m.set(a[idField] as string, sm);
  }
  return m;
}

// v3 speaker → display string (actor name, or kind[:label]).
function speakerLabel(speaker: unknown, actorNames: Map<string, string>): string {
  if (!isObj(speaker)) return "";
  if (typeof speaker.actor_id === "string") return actorNames.get(speaker.actor_id) ?? speaker.actor_id;
  if (typeof speaker.kind === "string") return `${speaker.kind}${speaker.label ? `:${S(speaker.label)}` : ""}`;
  return "";
}

// Transform a v3 script into the scaffold review-model consumed by the UI JS.
function toReviewModel(doc: Obj): Obj {
  const actorNames = nameMap(doc.actors, "actor_id", "actor_name");
  const locNames = nameMap(doc.locations, "location_id", "location_name");
  const propNames = nameMap(doc.props, "prop_id", "prop_name");
  const actorStates = stateNameMap(doc.actors, "actor_id");

  const episodes = (isArr(doc.episodes) ? doc.episodes : []).filter(isObj).map((ep) => ({
    episode: epNum(S(ep.episode_id)),
    synopsis: S(ep.synopsis),
    scenes: (isArr(ep.scenes) ? ep.scenes : []).filter(isObj).map((sc) => {
      const loc = isObj(sc.location) ? (locNames.get(S(sc.location.location_id)) ?? S(sc.location.location_id)) : "";
      const anames: string[] = [];
      const astates: Record<string, string> = {};
      for (const r of isArr(sc.actors) ? sc.actors : []) {
        if (!isObj(r) || typeof r.actor_id !== "string") continue;
        const nm = actorNames.get(r.actor_id) ?? r.actor_id;
        if (!anames.includes(nm)) anames.push(nm);
        // Show the state's name whenever it HAS one — including a named `default`.
        // In the video path `default` is the character's real main outfit (e.g.
        // 「京北大学白色球衣」), not an anonymous baseline, so excluding it hid the
        // dominant look under 「未标注」. A `default` with an empty name (the text
        // path's nameless baseline) still falls through to 未标注 as before.
        if (typeof r.state_id === "string") {
          const sn = actorStates.get(r.actor_id)?.get(r.state_id) ?? "";
          const label = sn || (r.state_id !== "default" ? r.state_id : "");
          if (label) astates[nm] = label;
        }
      }
      const pnames: string[] = [];
      for (const r of isArr(sc.props) ? sc.props : []) if (isObj(r) && typeof r.prop_id === "string") { const nm = propNames.get(r.prop_id) ?? r.prop_id; if (!pnames.includes(nm)) pnames.push(nm); }
      const env = isObj(sc.environment) ? sc.environment : {};
      return {
        scene_num: scnNum(S(sc.scene_id)),
        location_name: loc,
        environment: { space: SPACE_EN_TO_ZH[S(env.space)] ?? S(env.space), time: TIME_EN_TO_ZH[S(env.time)] ?? S(env.time) },
        actor_names: anames,
        actor_states: astates,
        prop_names: pnames,
        actions: (isArr(sc.actions) ? sc.actions : []).filter(isObj).map((a) => ({
          type: S(a.type),
          content: S(a.content),
          speaker: speakerLabel(a.speaker, actorNames),
          emotion: typeof a.emotion === "string" ? a.emotion : undefined,
          extend: isObj(a.extend) && typeof a.extend.timestamp === "string" ? { timestamp: a.extend.timestamp } : undefined,
        })),
      };
    }),
  }));

  const actors = (isArr(doc.actors) ? doc.actors : []).filter(isObj).map((a) => ({
    asset_name: S(a.actor_name),
    description: S(a.description),
    extend: {},
  }));
  return { title: S(doc.title), actors, episodes };
}

export function renderReviewHtml(doc: unknown): string {
  if (!isObj(doc)) throw new Error("review renderer expects a script object");
  const model = toReviewModel(doc);
  const data = JSON.stringify(model);
  const title = S(model.title);

  return `<!DOCTYPE html>
<html lang="zh">
<head>
<meta charset="utf-8">
<title>剧本审阅器 · ${title}</title>
<style>
  * { box-sizing: border-box; }
  html, body { margin: 0; height: 100%; font: 14px/1.6 -apple-system, "PingFang SC", system-ui, sans-serif; color: #1a1a1a; }
  body { display: flex; flex-direction: column; height: 100vh; background: #f5f5f7; }
  header { display: flex; align-items: center; gap: 12px; padding: 8px 14px; background: #fff; border-bottom: 1px solid #ddd; flex: none; }
  header h1 { font-size: 15px; margin: 0; font-weight: 600; }
  header select, header .nav { font-size: 13px; padding: 4px 8px; border: 1px solid #ccc; border-radius: 6px; background: #fff; cursor: pointer; }
  header .spacer { flex: 1; }
  header .hint { color: #888; font-size: 12px; }
  main { flex: 1; display: flex; min-height: 0; }
  .left { width: 42%; min-width: 320px; max-width: 620px; background: #000; display: flex; flex-direction: column; align-items: center; justify-content: flex-start; position: relative; }
  .left video { width: 100%; height: auto; max-height: 72vh; background: #000; }
  .vbar { color: #ccc; font-size: 12px; padding: 6px 10px; width: 100%; display: flex; gap: 12px; align-items: center; background: #111; }
  .vbar b { color: #fff; font-variant-numeric: tabular-nums; }
  .right { flex: 1; overflow-y: auto; padding: 10px 4px 40vh 4px; background: #fafafa; }
  .syn { background: #fff7e6; border: 1px solid #ffe0a3; border-radius: 8px; padding: 10px 12px; margin: 8px 10px 14px; color: #5b4a1f; font-size: 13px; }
  .scene { margin: 0 10px 14px; border: 1px solid #e5e5e5; border-radius: 8px; overflow: hidden; background: #fff; }
  .scene-head { background: #eef2f7; padding: 6px 12px; font-size: 12px; color: #445; display: flex; gap: 14px; flex-wrap: wrap; }
  .scene-head .loc { font-weight: 600; color: #223; }
  .rows { }
  .row { display: flex; gap: 10px; padding: 5px 12px; border-top: 1px solid #f0f0f0; cursor: pointer; align-items: baseline; }
  .row:hover { background: #eef6ff; }
  .row.active { background: #d8ecff; }
  .row .ts { flex: none; width: 46px; color: #3a7; font-variant-numeric: tabular-nums; font-size: 12px; }
  .row .kind { flex: none; width: 58px; font-size: 12px; }
  .row .kind.act { color: #999; }
  .row .kind.dlg { color: #c60; font-weight: 600; }
  .row .kind.think { color: #96c; font-style: italic; }
  .row .body { flex: 1; }
  .row .spk { color: #c60; font-weight: 600; margin-right: 4px; }
  .row .emo { color: #a8a; font-size: 12px; margin-left: 4px; }
  .row .body .subtitle { color: #b00; } /* 画面字幕引述，红色标出 */
  /* ===== Tab ===== */
  header .tabs { display: flex; gap: 4px; }
  header .tab { font-size: 13px; padding: 5px 14px; border: 1px solid #ccc; border-radius: 6px; background: #fff; cursor: pointer; color: #555; }
  header .tab.active { background: #1a6dff; color: #fff; border-color: #1a6dff; font-weight: 600; }
  header .ep-ctl { display: flex; gap: 8px; align-items: center; }
  /* ===== 资产总览 ===== */
  .assets { flex: 1; overflow-y: auto; padding: 16px 18px 30vh; background: #f5f5f7; }
  .asset-sec { margin: 0 0 26px; }
  .asset-sec > h2 { font-size: 15px; margin: 4px 0 12px; color: #223; display: flex; align-items: center; gap: 8px; }
  .asset-sec > h2 .cnt { font-size: 12px; font-weight: 400; color: #888; background: #ececf0; padding: 1px 8px; border-radius: 10px; }
  .acard { background: #fff; border: 1px solid #e3e3e6; border-radius: 10px; padding: 14px 16px; margin-bottom: 12px; }
  .acard .ah { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
  .acard .ah .nm { font-size: 17px; font-weight: 700; color: #1a1a1a; }
  .acard .ah .id { font-size: 12px; color: #aaa; font-variant-numeric: tabular-nums; }
  .acard .ah .meta { margin-left: auto; font-size: 12px; color: #888; display: flex; gap: 6px; }
  .acard .ah .meta .tag { background: #f0f0f3; border-radius: 6px; padding: 1px 8px; }
  .acard .desc { font-size: 13px; color: #555; margin: 7px 0 4px; }
  .acard .anchor { font-size: 12px; color: #999; margin-bottom: 8px; }
  /* 状态×集 矩阵 */
  .mtx { overflow-x: auto; margin-top: 8px; border: 1px solid #eee; border-radius: 8px; }
  table.tl { border-collapse: collapse; font-size: 12px; width: 100%; }
  table.tl th, table.tl td { border: 1px solid #eee; padding: 5px 7px; text-align: center; white-space: nowrap; }
  table.tl thead th { background: #f7f7f9; color: #556; font-weight: 600; position: sticky; top: 0; }
  table.tl .stname { text-align: left; font-weight: 600; color: #333; background: #fafafa; position: sticky; left: 0; min-width: 130px; max-width: 220px; white-space: normal; }
  table.tl .stname.unmk { color: #999; font-weight: 400; }
  table.tl .stname .dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 6px; vertical-align: middle; }
  table.tl .stname .sc { color: #aaa; font-weight: 400; font-size: 11px; margin-left: 4px; }
  table.tl td.cell { color: #ccc; }
  table.tl td.hit { background: #fff0f0; }
  table.tl td.hit.unmk { background: #f2f2f2; }
  .scc { display: inline-block; min-width: 20px; margin: 1px; padding: 1px 6px; border-radius: 6px; font-variant-numeric: tabular-nums; cursor: pointer; }
  .scc.on { background: #f4c9c9; color: #7a1f1f; border: 1px solid #e8a9a9; }
  .scc.on.unmk { background: #dcdcdc; color: #555; border-color: #c9c9c9; }
  .scc.on:hover { filter: brightness(0.94); }
</style>
</head>
<body>
<header>
  <h1>${title || "剧本审阅器"}</h1>
  <div class="tabs">
    <button class="tab active" id="tab-script">剧本审阅</button>
    <button class="tab" id="tab-assets">资产总览</button>
  </div>
  <span class="ep-ctl">
    <select id="epsel"></select>
    <button class="nav" id="prev">◀ 上一集</button>
    <button class="nav" id="next">下一集 ▶</button>
  </span>
  <span class="spacer"></span>
  <span class="hint" id="hint">点任意剧本行 → 视频跳到该时间戳</span>
</header>
<main id="view-script">
  <div class="left">
    <video id="vid" controls preload="metadata"></video>
    <div class="vbar"><span>当前 <b id="cur">00:00</b></span><span id="epinfo"></span></div>
  </div>
  <div class="right" id="right"></div>
</main>
<div id="view-assets" class="assets" style="display:none"></div>
<script>
const SCRIPT = ${data};
const eps = SCRIPT.episodes.slice().sort((a,b)=>a.episode-b.episode);
const vid = document.getElementById('vid');
const right = document.getElementById('right');
const epsel = document.getElementById('epsel');
const cur = document.getElementById('cur');
const epinfo = document.getElementById('epinfo');
let curEp = eps.length?eps[0].episode:1;

function pad(n){return String(n).padStart(2,'0');}
function fmt(sec){sec=Math.max(0,sec|0);return pad(sec/60|0)+':'+pad(sec%60);}
function tsToSec(ts){if(!ts)return null;const m=String(ts).match(/(?:(\\d{1,2}):)?(\\d{1,2}):(\\d{2}(?:\\.\\d{1,3})?)/);return m?(+(m[1]||0))*3600+(+m[2])*60+(+m[3]):null;}
function epId(n){return 'ep_'+String(n).padStart(3,'0');}
function actKind(t){return t==='dialogue'?'dlg':t==='inner_thought'?'think':'act';}
function actTs(a){return a && a.extend ? a.extend.timestamp : undefined;}

function renderBody(content){
  const esc = content.replace(/&/g,'&amp;').replace(/</g,'&lt;');
  return esc.replace(/(画面(?:字幕|文字)[:：][^。！？\\n]*)/g,'<span class="subtitle">$1</span>');
}

for(const e of eps){
  const o=document.createElement('option');
  o.value=e.episode; o.textContent='第 '+e.episode+' 集';
  epsel.appendChild(o);
}

function loadEp(n, targetScene){
  curEp=n;
  epsel.value=n;
  vid.src='src/'+epId(n)+'.mp4';
  const ep=eps.find(e=>e.episode===n);
  if(!ep)return;
  epinfo.textContent='第'+n+'集 · '+ep.scenes.length+'场';
  right.innerHTML='';
  if(ep.synopsis){const s=document.createElement('div');s.className='syn';s.textContent='梗概：'+ep.synopsis;right.appendChild(s);}
  for(const sc of ep.scenes){
    const box=document.createElement('div');box.className='scene';
    box.dataset.scene=sc.scene_num;
    const head=document.createElement('div');head.className='scene-head';
    const env=(sc.environment&&(sc.environment.space||'')+' '+(sc.environment.time||''))||'';
    const chNames=sc.actor_names||[]; const chSt=sc.actor_states||{};
    head.innerHTML='<span class="loc">场景'+sc.scene_num+' · '+(sc.location_name||'')+'</span>'+
      '<span>'+env+'</span>'+
      (chNames.length?'<span>角色: '+chNames.map(nm=>nm+(chSt[nm]?'('+chSt[nm]+')':'')).join('、')+'</span>':'');
    box.appendChild(head);
    const rows=document.createElement('div');rows.className='rows';
    for(const a of sc.actions){
      const ts=actTs(a); const kd=actKind(a.type); const sec=tsToSec(ts);
      const row=document.createElement('div');row.className='row';row.dataset.sec=sec==null?'':sec;
      const spk=a.speaker?'<span class="spk">'+a.speaker+'</span>':'';
      const emo=a.emotion?'<span class="emo">['+a.emotion+']</span>':'';
      row.innerHTML='<span class="ts">'+(ts||'')+'</span>'+
        '<span class="kind '+kd+'">'+kd+'</span>'+
        '<span class="body">'+spk+renderBody(a.content)+emo+'</span>';
      if(sec!=null)row.addEventListener('click',()=>{vid.currentTime=sec;vid.play();});
      rows.appendChild(row);
    }
    box.appendChild(rows);right.appendChild(box);
  }
  if(targetScene){
    const tb=right.querySelector('.scene[data-scene="'+targetScene+'"]');
    if(tb){tb.scrollIntoView({block:'start'}); tb.style.outline='2px solid #1a6dff'; setTimeout(()=>tb.style.outline='',1600);}
  } else { right.scrollTop=0; }
}

vid.addEventListener('timeupdate',()=>{
  cur.textContent=fmt(vid.currentTime);
  const t=vid.currentTime;
  const rows=[...right.querySelectorAll('.row')].filter(r=>r.dataset.sec!=='');
  let active=null;
  for(const r of rows){ if(+r.dataset.sec<=t+0.25) active=r; else break; }
  right.querySelectorAll('.row.active').forEach(r=>r.classList.remove('active'));
  if(active){active.classList.add('active');}
});

epsel.addEventListener('change',()=>loadEp(+epsel.value));
document.getElementById('prev').addEventListener('click',()=>{const i=eps.findIndex(e=>e.episode===curEp);if(i>0)loadEp(eps[i-1].episode);});
document.getElementById('next').addEventListener('click',()=>{const i=eps.findIndex(e=>e.episode===curEp);if(i<eps.length-1)loadEp(eps[i+1].episode);});

if(eps.length)loadEp(curEp);

// ===================== 资产总览 =====================
const allEps = eps.map(e=>e.episode).sort((a,b)=>a-b);
const actorMeta = {};
for(const a of (SCRIPT.actors||[])){
  const ext=a.extend||{};
  actorMeta[a.asset_name]={role:a.description||'',anchor:ext.anchor||''};
}

function aggAssets(){
  const person={}, loc={}, prop={};
  const addP=(nm,st,n,idx)=>{(person[nm]=person[nm]||{});(person[nm][st]=person[nm][st]||new Map()).set(n+':'+idx,{n,idx});};
  const addX=(bag,nm,n,idx)=>{(bag[nm]=bag[nm]||new Map()).set(n+':'+idx,{n,idx});};
  for(const ep of eps){ const n=ep.episode;
    for(const sc of ep.scenes){ const idx=sc.scene_num;
      const names=sc.actor_names||[]; const st=sc.actor_states||{};
      for(const nm of names) addP(nm, st[nm]||'未标注', n, idx);
      if(sc.location_name) addX(loc, sc.location_name, n, idx);
      for(const p of (sc.prop_names||[])) addX(prop, p, n, idx);
    }
  }
  return {person,loc,prop};
}
const AG = aggAssets();

function epsOf(occMaps){ const s=new Set(); for(const m of occMaps) for(const {n} of m.values()) s.add(n); return [...s].sort((a,b)=>a-b); }
function sceneChips(occMap, unmk){
  const byEp={}; for(const {n,idx} of occMap.values()){(byEp[n]=byEp[n]||[]).push(idx);}
  return byEp;
}
function chipEl(n, idx, unmk){
  const c=document.createElement('span'); c.className='scc on'+(unmk?' unmk':''); c.textContent=idx;
  c.title='第'+n+'集 · 场'+idx+' → 跳转';
  c.addEventListener('click',(e)=>{e.stopPropagation();gotoScene(n,idx);});
  return c;
}
function gotoScene(n, idx){ switchTab('script'); loadEp(n, idx); }

function personCard(name){
  const states=AG.person[name]; const stKeys=Object.keys(states);
  const involved=epsOf(Object.values(states));
  const card=document.createElement('div'); card.className='acard';
  const meta=actorMeta[name]||{};
  const totalScenes=new Set(); for(const m of Object.values(states)) for(const k of m.keys()) totalScenes.add(k);
  const head=document.createElement('div'); head.className='ah';
  head.innerHTML='<span class="nm">'+esc(name)+'</span>'+
    '<span class="meta"><span class="tag">'+totalScenes.size+' 场</span><span class="tag">'+involved.length+' 集</span><span class="tag">'+stKeys.length+' 状态</span></span>';
  card.appendChild(head);
  if(meta.role){const d=document.createElement('div');d.className='desc';d.textContent=meta.role;card.appendChild(d);}
  if(meta.anchor){const d=document.createElement('div');d.className='anchor';d.textContent='锚点：'+meta.anchor;card.appendChild(d);}
  const wrap=document.createElement('div'); wrap.className='mtx';
  const tb=document.createElement('table'); tb.className='tl';
  let thead='<thead><tr><th class="stname">状态＼集</th>';
  for(const n of involved) thead+='<th>'+n+'</th>';
  thead+='</tr></thead>';
  const ordered=stKeys.filter(s=>s!=='未标注').sort((a,b)=>states[b].size-states[a].size);
  if(states['未标注']) ordered.push('未标注');
  const tbody=document.createElement('tbody');
  for(const st of ordered){
    const unmk=st==='未标注';
    const byEp=sceneChips(states[st], unmk);
    const tr=document.createElement('tr');
    const th=document.createElement('td'); th.className='stname'+(unmk?' unmk':'');
    th.innerHTML='<span class="dot" style="background:'+(unmk?'#bbb':'#d9534f')+'"></span>'+esc(st)+'<span class="sc">'+states[st].size+'</span>';
    tr.appendChild(th);
    for(const n of involved){
      const td=document.createElement('td'); td.className='cell'+(byEp[n]?' hit'+(unmk?' unmk':''):'');
      if(byEp[n]){ for(const idx of byEp[n].sort((a,b)=>a-b)) td.appendChild(chipEl(n,idx,unmk)); }
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  }
  tb.innerHTML=thead; tb.appendChild(tbody);
  wrap.appendChild(tb); card.appendChild(wrap);
  return card;
}

function occCard(name, occMap){
  const involved=epsOf([occMap]);
  const card=document.createElement('div'); card.className='acard';
  const head=document.createElement('div'); head.className='ah';
  head.innerHTML='<span class="nm">'+esc(name)+'</span>'+
    '<span class="meta"><span class="tag">'+occMap.size+' 场</span><span class="tag">'+involved.length+' 集</span></span>';
  card.appendChild(head);
  const wrap=document.createElement('div'); wrap.className='mtx';
  const tb=document.createElement('table'); tb.className='tl';
  let thead='<thead><tr><th class="stname">出现＼集</th>';
  for(const n of involved) thead+='<th>'+n+'</th>'; thead+='</tr></thead>';
  const byEp=sceneChips(occMap,false);
  const tbody=document.createElement('tbody'); const tr=document.createElement('tr');
  const th=document.createElement('td'); th.className='stname';
  th.innerHTML='<span class="dot" style="background:#5b8def"></span>出现';
  tr.appendChild(th);
  for(const n of involved){ const td=document.createElement('td'); td.className='cell'+(byEp[n]?' hit':'');
    if(byEp[n]){ for(const idx of byEp[n].sort((a,b)=>a-b)) td.appendChild(chipEl(n,idx,false)); } tr.appendChild(td);}
  tbody.appendChild(tr); tb.innerHTML=thead; tb.appendChild(tbody);
  wrap.appendChild(tb); card.appendChild(wrap);
  return card;
}

function esc(s){return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;');}

let assetsRendered=false;
function renderAssets(){
  if(assetsRendered) return; assetsRendered=true;
  const root=document.getElementById('view-assets');
  const persons=Object.keys(AG.person).sort((a,b)=>{
    const ea=new Set(),eb=new Set();
    for(const m of Object.values(AG.person[a])) for(const {n} of m.values()) ea.add(n);
    for(const m of Object.values(AG.person[b])) for(const {n} of m.values()) eb.add(n);
    return eb.size-ea.size;
  });
  const secP=document.createElement('div'); secP.className='asset-sec';
  secP.innerHTML='<h2>人物 <span class="cnt">'+persons.length+'</span></h2>';
  for(const nm of persons) secP.appendChild(personCard(nm));
  root.appendChild(secP);
  const locs=Object.keys(AG.loc).sort((a,b)=>AG.loc[b].size-AG.loc[a].size);
  const secL=document.createElement('div'); secL.className='asset-sec';
  secL.innerHTML='<h2>场景 <span class="cnt">'+locs.length+'</span></h2>';
  for(const nm of locs) secL.appendChild(occCard(nm,AG.loc[nm]));
  root.appendChild(secL);
  const props=Object.keys(AG.prop).sort((a,b)=>AG.prop[b].size-AG.prop[a].size);
  const secD=document.createElement('div'); secD.className='asset-sec';
  secD.innerHTML='<h2>道具 <span class="cnt">'+props.length+'</span></h2>';
  for(const nm of props) secD.appendChild(occCard(nm,AG.prop[nm]));
  root.appendChild(secD);
}

const tabScript=document.getElementById('tab-script'), tabAssets=document.getElementById('tab-assets');
const viewScript=document.getElementById('view-script'), viewAssets=document.getElementById('view-assets');
const epCtl=document.querySelector('.ep-ctl'), hint=document.getElementById('hint');
function switchTab(which){
  const asset=which==='assets';
  tabScript.classList.toggle('active',!asset); tabAssets.classList.toggle('active',asset);
  viewScript.style.display=asset?'none':'flex'; viewAssets.style.display=asset?'block':'none';
  epCtl.style.display=asset?'none':'flex';
  hint.textContent=asset?'点场号 → 跳回剧本审阅对应集/场':'点任意剧本行 → 视频跳到该时间戳';
  if(asset) renderAssets();
}
tabScript.addEventListener('click',()=>switchTab('script'));
tabAssets.addEventListener('click',()=>switchTab('assets'));
</script>
</body>
</html>`;
}
